import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import AITutor from "@/components/AITutor";
import Landing from "@/pages/Landing";
import Modules from "@/pages/Modules";
import ModuleDetail from "@/pages/ModuleDetail";
import Chapter from "@/pages/Chapter";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Bookmarks from "@/pages/Bookmarks";
import Certificate from "@/pages/Certificate";
import PublicCertificate from "@/pages/PublicCertificate";
import ProfileSettings from "@/pages/ProfileSettings";
import AllContributions from "@/pages/AllContributions";

function Protected({ children }) {
  const { user, checking } = useAuth();
  const loc = useLocation();
  if (checking) return <div className="max-w-3xl mx-auto p-12 text-[#525252]">Loading…</div>;
  if (!user || user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function Shell() {
  return (
    <div className="min-h-screen bg-[#FFFFFF] relative">
      <Navbar />
      <main className="relative z-10">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/modules" element={<Modules />} />
          <Route path="/modules/:moduleId" element={<ModuleDetail />} />
          <Route path="/modules/:moduleId/chapters/:chapterId" element={<Chapter />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/bookmarks" element={<Protected><Bookmarks /></Protected>} />
          <Route path="/certificate/:moduleId" element={<Protected><Certificate /></Protected>} />
          <Route path="/c/:certId" element={<PublicCertificate />} />
          <Route path="/profile" element={<Protected><ProfileSettings /></Protected>} />
          <Route path="/contributions" element={<AllContributions />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <AITutor />
      <footer className="mt-24 border-t border-[#E5E5E0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <div>
              <div className="font-serif text-2xl font-bold text-[#0A0A0A] tracking-tight">YesVersity</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#525252] mt-1">India · Dubai</div>
              <p className="text-sm text-[#525252] mt-4 leading-relaxed max-w-xs">
                Long-form real estate education for investors and agents across two markets — written natively, not translated.
              </p>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-3">Curriculum</div>
              <ul className="space-y-2 text-sm">
                <li><a href="/modules#foundations" className="text-[#0A0A0A] hover:text-[#C5A059] transition-colors">Foundations</a></li>
                <li><a href="/modules#india" className="text-[#0A0A0A] hover:text-[#C5A059] transition-colors">India Track</a></li>
                <li><a href="/modules#dubai" className="text-[#0A0A0A] hover:text-[#C5A059] transition-colors">Dubai Track</a></li>
              </ul>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-3">Account</div>
              <ul className="space-y-2 text-sm">
                <li><a href="/login" className="text-[#0A0A0A] hover:text-[#C5A059] transition-colors">Sign in</a></li>
                <li><a href="/register" className="text-[#0A0A0A] hover:text-[#C5A059] transition-colors">Create account</a></li>
                <li><a href="/dashboard" className="text-[#0A0A0A] hover:text-[#C5A059] transition-colors">Dashboard</a></li>
              </ul>
            </div>
          </div>

          <div data-testid="footer-disclaimer" className="border-t border-[#E5E5E0] pt-8 text-xs leading-relaxed text-[#525252] max-w-4xl">
            <div className="font-mono uppercase tracking-[0.2em] text-[#0A0A0A] mb-3">Disclaimer</div>
            <p className="mb-3">
              The content on YesVersity is provided for <strong className="text-[#0A0A0A]">educational and informational purposes only</strong> and does not constitute investment, tax, legal, or professional advice. Real estate markets — including those in India and Dubai — carry inherent risks, and past performance is not indicative of future returns.
            </p>
            <p className="mb-3">
              Figures, rates, regulations (RERA, DLD, GST, EIBOR, LTV caps, tax provisions) and market conditions cited in the chapters may change without notice. Readers should <strong className="text-[#0A0A0A]">independently verify all information</strong> and consult a licensed real estate professional, chartered accountant, tax adviser, or legal counsel before making any purchase, sale, financing, or portfolio decision.
            </p>
            <p>
              YesVersity is not a registered investment adviser, real estate brokerage, mortgage originator, or law firm in any jurisdiction. Nothing on this platform should be interpreted as a solicitation, recommendation, or offer to buy or sell any property or security.
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-[#E5E5E0] flex flex-col md:flex-row justify-between gap-3 text-xs font-mono uppercase tracking-wider text-[#525252]">
            <div>© {new Date().getFullYear()} YesVersity · All rights reserved</div>
            <div>Made with rigour · India × Dubai</div>
          </div>
        </div>
      </footer>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
